import React, { useState, useEffect } from 'react';
import bindDeviceActions from '../device/redux/bindDeviceActions';
import { deviceIsMine, guid } from '../../common/ambient';
import { useDevice } from '../device/redux/hooks';
import { DeviceChooser } from '../../components';
import classNames from 'classnames';
import AlertForm from './AlertForm';
import AlertDescription from './AlertDescription';
import DeviceName from '../device/DeviceName';
import "./AlertSet.less";
import { ALERT_FREQS, getAlertFreqArr } from './alertConstants';
import {
  alertSetOk,
  getAlertParamsForDevice
} from '.'
// import PropTypes from 'prop-types';

const createAlert = () => {
  return {
    id: guid(),
    param: '',
    condition: '',
    threshold: '',
    freq: '10min'
  };
}

const AlertSet = bindDeviceActions(({ alertSet, editing, onChange, onCancel, device, actions }) => {
  const [alertSetState, setAlertSetState] = useState(alertSet);
  const [alertEditing, setAlertEditing] = useState(null);
  const [newAlert, setNewAlert] = useState(createAlert());
  const [showAlertForm, setShowAlertForm] = useState(editing && alertSet.alerts && alertSet.alerts.length === 0);
  const { title, description, alerts = [], deviceIds = [], type } = alertSetState;
  const devices = useDevice(device, actions, deviceIds);
  const deviceChooserRef = React.useRef(null);

  const MAX_ALERTS = 10;

  useEffect(() => {
    setAlertSetState(alertSet);
  }, [alertSet, setAlertSetState]);


  const handleTitleChange = (e) => {
    setAlertSetState({ ...alertSetState, title: e.target.value });
  };

  const handleDescriptionChange = (e) => {
    setAlertSetState({ ...alertSetState, description: e.target.value });
  };
  const shouldShowAlertForm = editing && deviceIds[0] && (alerts.length < MAX_ALERTS || alertEditing) && (showAlertForm || alertEditing)

  let problemAlerts = alerts.filter(a => getAlertParamsForDevice(devices[0]).indexOf(a.param) === -1);
  if (deviceIds.length === 0) {
    problemAlerts = []
  }
  const hasDevices = deviceIds.length > 0;

  return (
    <div className={classNames('alert-set device-widget', type, { editing, template: !hasDevices })}>
      <header>
        {editing ? (
          <input 
            className='form-control'
            type="text" 
            value={title} 
            onChange={handleTitleChange} 
            placeholder="Title" 
          />
        ) : (
          title
        )}
      </header>
      <div className='body'>
        {editing ? (
          <textarea 
            className='form-control'
            value={description} 
            onChange={handleDescriptionChange} 
            placeholder="Description" 
          />
        ) : (
          <p>{description}</p>
        )}
        {(hasDevices || editing) && (
          <>
            <h4>Station</h4>
            <div className='device-chooser-wrap'>
              {deviceIds.map(deviceId => (
                <div
                  key={deviceId}
                  onClick={() => {
                    editing && deviceChooserRef.current && deviceChooserRef.current.toggleOpen()
                  }}
                  style={{ cursor: editing ? 'pointer' : 'default' }}
                >
                  <DeviceName deviceId={deviceId} />
                </div>
              ))}
              {editing && (
                <DeviceChooser
                  onRef={ref => deviceChooserRef.current = ref}
                  devicesFilter={device => device.lastData}
                  onSelect={d => {
                    setAlertSetState({
                      ...alertSetState,
                      deviceIds: [d._id],
                      alerts: alerts.map(a => ({ ...a, deviceId: d._id, id: guid() }))
                    });
                  }}
                />
              )}
            </div>
            <h4>Notification time window</h4>
            {editing ? (
              <select className='form-control' onChange={e => {
                setAlertSetState({ 
                  ...alertSetState, 
                  freq: e.target.value,
                  alerts: alerts.map(a => ({ ...a, freq: e.target.value, id: guid() }))
                })
              }} value={alertSetState.freq}>
                {ALERT_FREQS.map(freq => (
                  <option key={freq[1]} value={freq[1]}>{freq[0]}</option>
                ))}
              </select>
            ) : (
              <p>{getAlertFreqArr(alertSetState.freq)[0]}</p>
            )}
          </>
        )}
        <h4>
          {shouldShowAlertForm ? (alertEditing ? 'Edit alert' : 'Add a new alert...') : 'Alerts'}
          {editing && !shouldShowAlertForm && alerts.length < MAX_ALERTS && deviceIds[0] && (
            <a onClick={() => setShowAlertForm(true)} style={{ fontSize: '0.8em', marginLeft: '10px', cursor: 'pointer' }}>Add</a>
          )}
          {shouldShowAlertForm && (
            <a onClick={() => {
              setAlertEditing(null);
              setShowAlertForm(false);
            }} style={{ fontSize: '0.8em', marginLeft: '10px', cursor: 'pointer' }}>Cancel</a>
          )}
        </h4>
        {shouldShowAlertForm && (
          <AlertForm 
            currentDevice={devices[0]} 
            onChange={newAlert => {
              newAlert.freq = alertSetState.freq || '10min';
              setAlertSetState({
                ...alertSetState,
                alerts: [...alertSetState.alerts.filter(a => a.id !== alertEditing), newAlert]
              });
              setNewAlert(createAlert());
              setAlertEditing(null);
              setShowAlertForm(false);
            }} 
            alert={alertEditing ? alerts.find(a => a.id === alertEditing) : null}
          />
        )}
        <div className='alerts'>
          {alerts.map(alert => (
            <div key={alert.id} className={classNames('alert-description-wrap', { 
              active: alertEditing === alert.id,
              warn: problemAlerts.indexOf(alert) !== -1
            })}>
              <AlertDescription alrt={alert} />
              <div className='actions'>
                <a onClick={() => {
                  setAlertEditing(alert.id);
                  setShowAlertForm(true);
                }}><i className='glyphicon glyphicon-pencil' /></a>
                <a onClick={() => setAlertSetState({ ...alertSetState, alerts: alerts.filter(a => a.id !== alert.id) })}><i className='glyphicon glyphicon-trash' /></a>
              </div>
            </div>
          ))}
          {problemAlerts.length > 0 && (
            <div className='warn-message'>
              Please note that some alerts are not supported from the selected station. Remove them from the list or choose different nearby station.
              {deviceIsMine(device, deviceIds[0]) && (
                <a className='upgrade' href="https://ambientweather.com" target="_blank">Upgrade this station</a>
              )}
            </div>
          )}
        </div>
      </div>
      <div className='btns'>
        <button
          className='btn btn-primary'
          disabled={!alertSetOk(alertSetState) || alertEditing || showAlertForm}
          onClick={() => onChange(alertSetState)}
        >
          Save
        </button>
        <a onClick={() => {
          setAlertSetState(alertSet);
          onCancel && onCancel();
        }}>cancel</a>
      </div>
    </div>
  );
})
export default AlertSet;

AlertSet.propTypes = {};
AlertSet.defaultProps = {};

AlertSet.displayName = 'AlertSet'
